import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import api from "@helpers/api.js";
import Parser from "@helpers/parser";
import { useWallets, walletIsConnected } from "@helpers/wallets";

import "./styles.css";

import CustomModal from "@components/CustomModal/index.js";

import SendIcon from "@mui/icons-material/Send";

import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import {
    AccountBalanceRounded,
    AttachMoneyRounded,
    AutoModeRounded,
    CreditCardRounded,
    PixRounded,
    RadioButtonUnchecked,
} from "@mui/icons-material";

import {
    IconBolt,
    IconCalendarDollar,
    IconCalendarRepeat,
    IconLock,
    IconMapPin,
    IconWorld,
} from "@tabler/icons-react";


import { Input, Text, ErrorMsg, InfoBlock } from '@fragments'

import CheckoutPreview from './CheckoutPreview'
import { activeMethodKeys, mapApiValidationErrors, validatePlanConfig } from './validation'

// Labels are resolved at call time so they follow the active language; each
// `interval` is the API keyword and must never be translated.
const getIntervalsConfig = (t) => [
    {
        interval: "week",
        label: t("common:interval.adjective.week"),
    },
    {
        interval: "month",
        label: t("common:interval.adjective.month"),
    },
    {
        interval: "bimonthly",
        label: t("common:interval.adjective.bimonthly"),
    },
    {
        interval: "quarterly",
        label: t("common:interval.adjective.quarterly"),
    },
    {
        interval: "semiannual",
        label: t("common:interval.adjective.semiannual"),
    },
    {
        interval: "year",
        label: t("common:interval.adjective.year"),
    },
    {
        "interval": "lifetime",
        "label": t("common:interval.adjective.lifetime")
    }
];

// Fluxo antecipado: todas as parcelas são antecipadas a partir de D+7 e os
// juros vão para o comprador. Prolongado: cada parcela entra no saldo mês a
// mês, sem juros. Sem a chave, vale o padrão do core (antecipado).
const isAnticipated = (config) => config?.anticipation?.enabled !== false;

export default function ConfigPlanModal({ modalRef, config }) {
    const { t } = useTranslation("contentModals");
    const intervalsConfig = useMemo(() => getIntervalsConfig(t), [t]);
    const [open, setOpen] = useState(false);

    const [loading, setLoading] = useState(false)

    const REF_config = useRef();

    const [saving, setSaving] = useState(false)
    const [info, setInfo] = useState(null)
    const [errors, setErrors] = useState({})
    const [alertError, setAlertError] = useState(null)
    const [simulation, setSimulation] = useState({ list: null, loading: false, error: null })
    // A prévia acompanha a seção de precificação que o usuário está editando.
    const [previewRegion, setPreviewRegion] = useState('national')

    const REF_simulationSeq = useRef(0)
    const REF_lastSimulationKey = useRef(null)
    const REF_previewColumn = useRef(null)

    // Centraliza a prévia verticalmente só quando ela cabe na área visível do
    // modal — sticky centralizado cortaria topo e rodapé de um card maior que
    // o scroll. Observa o card (lista de países expande) e o scrollport.
    const previewMounted = !!info

    useEffect(() => {
        if (!open || !previewMounted) return

        const column = REF_previewColumn.current
        if (!column || typeof ResizeObserver === 'undefined') return

        const scrollParent = column.closest('.custom-modal-body')
        if (!scrollParent) return

        const update = () => {
            const overflow = column.scrollHeight + 16 - scrollParent.clientHeight

            if (overflow <= 0) {
                column.style.top = ''
                column.classList.add('is-centered')
            } else {
                // Card maior que a área visível: top negativo faz a coluna
                // rolar junto até o rodapé aparecer e então grudar ali.
                column.classList.remove('is-centered')
                column.style.top = `${-overflow}px`
            }
        }

        const observer = new ResizeObserver(update)
        observer.observe(column)
        observer.observe(scrollParent)
        update()

        return () => observer.disconnect()
    }, [open, previewMounted])

    const REF_callback = useRef();
    const { asaas, stripe, loading: loadingWallets } = useWallets();
    const stripeConnected = walletIsConnected(stripe);
    const asaasConnected = walletIsConnected(asaas);

    modalRef.current = {
        open({ id, community, mode }, callback) {
            REF_config.current = { id, community, mode };
            REF_callback.current = callback;
            setInfo(null)
            setErrors({})
            setAlertError(null)
            setSimulation({ list: null, loading: false, error: null })
            setPreviewRegion('national')
            REF_lastSimulationKey.current = null
            handleLoadPlan(id)
            setOpen(true);
        },
        close() {
            setOpen(false);
        },
    };

    const defaultConfig = {
        label: "Premium",
        interval: "year",
        installments: 12,
        price: 29700,
        usd_price: null,
        // Mesmo padrão do core quando a mecânica não é informada.
        anticipation: {
            enabled: true,
        },
        autorenew: true,
        enable_installments: true,
        methods: {
            pix: {
                enabled: true,
            },
            credit: {
                enabled: true,
            },
        },
    }

    const installmentsAvailable = getMaxInstallmentsAvailable();

    // Preço, período e renovação travam assim que o plano tem assinantes
    // ativos ou expirados — o core responde 422 à edição desses campos.
    const subscribersSummary = info?.subscribersSummary
    const pricingLocked = !!info?.id
        && ((subscribersSummary?.active ?? 0) + (subscribersSummary?.expired ?? 0)) > 0


    useEffect(() => {
        if (info?.interval === "lifetime") {
            handleUpdate('autorenew', false)
        }
    }, [info?.interval])

    async function simulateInstallments(config = info, { force = false } = {}) {
        if (!config) return

        const price = parseInt(config.price, 10) || 0
        const installments = config.enable_installments
            ? (config.installments || getMaxInstallmentsAvailable(price))
            : 1
        const anticipationEnabled = isAnticipated(config)

        if (!price) {
            REF_lastSimulationKey.current = null
            setSimulation({ list: null, loading: false, error: null })
            return
        }

        // Blur dispara mesmo sem mudanças — só consulta quando os insumos do
        // cálculo (preço, parcelas, antecipação) realmente mudaram.
        const simulationKey = `${price}|${installments}|${anticipationEnabled}`
        if (!force && simulationKey === REF_lastSimulationKey.current) return
        REF_lastSimulationKey.current = simulationKey

        const seq = ++REF_simulationSeq.current
        setSimulation(prev => ({ ...prev, loading: true, error: null }))

        try {
            // No fluxo antecipado o total parcelado inclui os juros repassados
            // ao comprador; no prolongado cada total é o próprio preço.
            const response = await api.post(
                `/community/${REF_config.current.community}/plan/simulateInstallments`,
                { price, installments, anticipation: { enabled: anticipationEnabled } }
            )

            if (seq !== REF_simulationSeq.current) return

            const list = response?.data?.installments_list

            if (!Array.isArray(list)) throw response

            setSimulation({ list, loading: false, error: null })
        } catch (e) {
            if (seq !== REF_simulationSeq.current) return

            REF_lastSimulationKey.current = null
            setSimulation(prev => ({ ...prev, loading: false, error: t('managePlan.feedback.simulationError') }))
        }
    }

    // A simulação inicial parte daqui (e não de um efeito) porque abrir o
    // modal duas vezes seguidas não altera `info` de forma observável.
    async function handleLoadPlan(id = null) {
        if (!id) {
            const next = { ...defaultConfig }
            setInfo(next)
            simulateInstallments(next)
            return
        }

        setLoading(true)

        try {
            // api.js resolve erros HTTP devolvendo o objeto de erro do axios,
            // então a falha é detectada pela ausência do recurso na resposta.
            const response = await api.get(`/community/plan/${id}`)
            const data = response?.data

            if (!data?.id) throw response

            // installments = 1 equivale a "sem parcelamento" (pagamento em 1x).
            const next = { ...data, enable_installments: Number(data.installments) > 1 }
            setInfo(next)
            simulateInstallments(next)
        } catch (e) {
            console.error(e)
            window.setSnackbar(t('managePlan.feedback.loadError'))
            setOpen(false)
        }

        setLoading(false)
    }

    function getMaxInstallmentsAvailable(value = info?.price) {
        if (!value) return 1;

        const minPricePerInstallment = 600; // R$ 6,00 em centavos
        const maxInstallments = 12;

        // Calcula quantas parcelas são possíveis com o preço mínimo
        const possibleInstallments = Math.floor(value / minPricePerInstallment);

        // Retorna o menor valor entre as parcelas possíveis e o máximo permitido
        const result = Math.min(Math.max(possibleInstallments, 1), maxInstallments);

        return result
    }

    const InputHandler = {
        method(selected) {
            // Preserva métodos que o modal não exibe (boleto/manual) para não
            // removê-los silenciosamente de planos existentes.
            handleUpdate('methods', {
                ...(info?.methods || {}),
                pix: {
                    ...(info?.methods?.pix || {}),
                    enabled: selected.includes('pix')
                },
                credit: {
                    ...(info?.methods?.credit || {}),
                    enabled: selected.includes('credit')
                }
            })
        },
        enableInstallments(value) {
            // Se está habilitando parcelamento (de false para true)
            if (value && !info?.enable_installments) {
                // Define installments com o máximo disponível
                const maxInstallments = getMaxInstallmentsAvailable(info?.price);
                const next = {
                    ...info,
                    enable_installments: value,
                    installments: maxInstallments
                };
                setInfo(next);
                simulateInstallments(next);
            } else {
                // Se está desabilitando, apenas atualiza enable_installments
                handleUpdate('enable_installments', value);
                simulateInstallments({ ...info, enable_installments: value });
            }
            setPreviewRegion('national')
        },
        interval(value) {
            handleUpdate('interval', value)
        },
        label(value) {
            handleUpdate('label', value)
        },
        price(value) {
            handleUpdate('price', value?.replace(/\D/g, ""))
            setPreviewRegion('national')
        },
        usdPrice(value) {
            handleUpdate('usd_price', value?.replace(/\D/g, ""))
            setPreviewRegion('international')
        },
        installments(value) {
            const installments = parseInt(value)
            handleUpdate('installments', installments)
            simulateInstallments({ ...info, installments })
            setPreviewRegion('national')
        },
        autorenew(value) {
            handleUpdate('autorenew', value)
        },
        receivablesFlow(value) {
            const next = {
                ...info,
                anticipation: {
                    ...info?.anticipation,
                    enabled: value === 'anticipated',
                },
            }
            setInfo(next)
            simulateInstallments(next)
            setPreviewRegion('national')
        }
    }

    function handleUpdate(key, value) {
        const priceCopy = { ...info };
        priceCopy[key] = value;
        if (key === 'price') {
            priceCopy['installments'] = getMaxInstallmentsAvailable(value)
        }
        setInfo({ ...priceCopy });

        setErrors(prev => {
            if (!prev || prev[key] === undefined) return prev
            const next = { ...prev }
            delete next[key]
            return next
        })
    }

    async function handleSubmit() {
        const isEdit = !!info?.id

        const { isValid, errors: validationErrors } = validatePlanConfig(info, {
            t,
            pricingLocked,
            requiresUsdPrice: stripeConnected,
        })

        setErrors(validationErrors)
        setAlertError(null)

        if (!isValid) {
            window.setSnackbar(t('managePlan.feedback.highlightFields'))
            return
        }

        const payload = {
            label: info.label.trim(),
            // O core rejeita installments null na criação — "sem parcelamento"
            // é enviado como 1 (pagamento em 1x), que também persiste a
            // desativação do parcelamento na edição.
            installments: info.enable_installments ? (info.installments || installmentsAvailable) : 1,
            methods: activeMethodKeys(info.methods),
        }

        if (isEdit) {
            payload.id = info.id
        } else {
            payload.community = REF_config.current.community
        }

        // Preço, período, renovação e mecânica de recebíveis só viajam quando
        // editáveis: com assinantes ativos/expirados o core responde 422 a
        // qualquer um deles, mesmo sem mudança de valor.
        if (!pricingLocked) {
            payload.price = info.price
            payload.interval = info.interval
            payload.autorenew = info.autorenew
            payload.anticipation = { enabled: isAnticipated(info) }
        }

        if (stripeConnected) {
            payload.usd_price = info.usd_price
        }

        setSaving(true)

        try {
            const method = isEdit ? 'put' : 'post'
            const endpoint = {
                post: `/community/${REF_config.current.community}/plan`,
                put: `/community/plan/${info.id}`
            }[method]

            // api.js resolve erros HTTP devolvendo o objeto de erro do axios —
            // sucesso é reconhecido pelo recurso salvo, falha pelo e.response.
            const response = await api[method](endpoint, payload)

            if (response?.data?.id) {
                try {
                    await REF_callback.current(response.data)
                } catch (callbackError) {
                    console.error(callbackError)
                }

                window.setSnackbar(t('managePlan.feedback.saved'))
                setOpen(false)
            } else {
                applyApiError(response)
            }
        } catch (e) {
            console.error(e)
            applyApiError(e)
        }

        setSaving(false)
    }

    function applyApiError(failure) {
        const status = failure?.response?.status
        const responseData = failure?.response?.data

        if (status === 422 && responseData?.errors) {
            const { fieldErrors, generalMessages } = mapApiValidationErrors(responseData.errors, {
                t,
                usdPriceFieldVisible: stripeConnected,
            })

            setErrors(fieldErrors)
            setAlertError({
                title: t('managePlan.errors.saveTitle'),
                message: generalMessages.length
                    ? generalMessages.join(' ')
                    : t('managePlan.errors.saveMessage'),
            })
            return
        }

        if (status === 403) {
            setAlertError({
                title: t('managePlan.errors.forbiddenTitle'),
                message: t('managePlan.errors.forbiddenMessage'),
            })
            return
        }

        if (status === 404) {
            setAlertError({
                title: t('managePlan.errors.notFoundTitle'),
                message: t('managePlan.errors.notFoundMessage'),
            })
            return
        }

        setAlertError({
            title: t('managePlan.errors.saveTitle'),
            message: t('managePlan.errors.unexpectedMessage'),
        })
    }



    const hasInstallmentsEnabled = !!info?.enable_installments

    const activeMethods = activeMethodKeys(info?.methods)

    // A mecânica de recebíveis só existe no crédito nacional (Asaas).
    const receivablesFlow = isAnticipated(info) ? 'anticipated' : 'extended'

    return (
        <CustomModal
            maxWidth={1000}
            bottomButtonSumbitText={t('managePlan.submitButton')}
            bottomButtonSumbitIcon={<SendIcon />}
            loading={loading || saving || loadingWallets}
            errorTitle={alertError?.title}
            errorMessage={alertError?.message}
            title={t('managePlan.modalTitle')}
            handleSubmit={handleSubmit}
            open={open}
            handleClose={() => setOpen(false)}
            bodyComponent={
                <div className="px-4 py-4 plan-modal-layout" >

                    {
                        info && <div
                            className="plan-config-container pt-3"
                            style={{ alignItems: "flex-start" }}
                        >
                            <Input.Text
                                label={t('managePlan.nameLabel')}
                                value={info.label}
                                onInput={InputHandler.label}
                                size={25}
                                error={!!errors.label}
                                helper={errors.label}
                                className="mr-1"
                            />


                            <Text.h3
                                value={t('managePlan.nationalPricing.title')}
                                className="mt-4 mb-1"
                                left={<IconMapPin />}
                            />
                            <Text.span
                                value={t('managePlan.nationalPricing.description')}
                                contrast={6}
                            />

                            {pricingLocked && (
                                <InfoBlock
                                    title={t('managePlan.pricingLock.title')}
                                    description={t('managePlan.pricingLock.description')}
                                />
                            )}

                            <Input.Money
                                disabled={pricingLocked}
                                label={t('managePlan.nationalPricing.priceLabel')}
                                placeholder={t('managePlan.nationalPricing.pricePlaceholder')}
                                className="mt-2"
                                value={Parser.price.brl(info?.price)}
                                onInput={InputHandler.price}
                                onBlur={() => simulateInstallments()}
                                error={!!errors.price}
                                helper={errors.price}
                            />

                            {!loadingWallets && !asaasConnected && (
                                // Sem carteira nacional aprovada o checkout em BRL fica
                                // bloqueado (gate payments.national) — o plano existe,
                                // mas ninguém consegue pagá-lo em reais.
                                <InfoBlock
                                    title={t('managePlan.wallet.pendingTitle')}
                                    description={t('managePlan.wallet.pendingDescription')}
                                    ctaText={t('managePlan.wallet.cta')}
                                    onCtaClick={() => {
                                        setOpen(false)
                                        window.navigate('/banking/wallet')
                                    }}
                                />
                            )}

                            {
                                !['week', 'month'].includes(info.interval) && (
                                    <div className="mt-1" key={Math.random()}>
                                        <Input.Switch
                                            label={t('managePlan.installments.switchLabel')}
                                            value={hasInstallmentsEnabled}
                                            onInput={InputHandler.enableInstallments}
                                        />

                                        {
                                            hasInstallmentsEnabled && (
                                                <Input.Select
                                                    key={Math.random()}
                                                    className="mt-1"
                                                    label={t('managePlan.installments.selectLabel')}
                                                    disabled={!hasInstallmentsEnabled}
                                                    value={hasInstallmentsEnabled ? (info.installments) : '-'}
                                                    onInput={InputHandler.installments}
                                                    options={Array.from({ length: installmentsAvailable }, (_, index) => ({
                                                        label: t('managePlan.installments.option', { count: index + 1 }),
                                                        value: index + 1,
                                                    }))}
                                                />
                                            )
                                        }
                                    </div>
                                )
                            }

                            {stripeConnected && (
                                <>
                                    <Text.h3
                                        value={t('managePlan.internationalPricing.title')}
                                        className="mt-4 mb-1"
                                        left={<IconWorld />}
                                    />

                                    <Input.Money
                                        start="US$"
                                        label={t('managePlan.internationalPricing.priceLabel')}
                                        placeholder="0.00"
                                        className="mt-2"
                                        value={Parser.price.usd(info?.usd_price)}
                                        onInput={InputHandler.usdPrice}
                                        error={!!errors.usd_price}
                                        helper={errors.usd_price}
                                    />
                                    <Text.caption
                                        contrast={6}
                                        className="mt-1"
                                    >
                                        {t('managePlan.internationalPricing.hint')}
                                    </Text.caption>
                                </>
                            )}



                            <Text.h3
                                value={t('managePlan.renewal.title')}
                                className="mt-4 mb-1"
                                left={<AutoModeRounded />}
                            />
                            <Text.span
                                value={t('managePlan.renewal.description')}
                                contrast={6}
                            />


                            <Input.Switch
                                key={`${info?.interval}-${info?.autorenew}`}
                                disabled={pricingLocked || info?.interval === "lifetime"}
                                label={t('managePlan.renewal.autorenewLabel')}
                                value={info?.autorenew}
                                onInput={InputHandler.autorenew}
                            />

                            <Input.Select
                                label={t('managePlan.renewal.intervalLabel')}
                                disabled={pricingLocked}
                                className="mt-1"
                                value={info.interval}
                                onInput={InputHandler.interval}
                                options={intervalsConfig.map((v) => ({
                                    label: v.label,
                                    value: v.interval,
                                }))}
                            />

                            <Text.h3
                                value={t('managePlan.methods.title')}
                                className="mt-4"
                                left={<AttachMoneyRounded />}
                            />
                            <Input.Choices
                                value={activeMethods}
                                className="mt-1"
                                onInput={InputHandler.method}
                                options={[
                                    {
                                        label: t('managePlan.methods.credit'),
                                        value: "credit",
                                        left: <CreditCardRounded fontSize="small" />
                                    },
                                    {
                                        label: t('managePlan.methods.pix'),
                                        value: "pix",
                                        left: <PixRounded fontSize="small" />
                                    }
                                ]}
                                multiple
                            />

                            {errors.methods && (
                                <ErrorMsg className="mt-1">{errors.methods}</ErrorMsg>
                            )}

                            {activeMethods.includes('credit') && (
                                <>
                                    <Text.h3
                                        value={t('managePlan.receivables.title')}
                                        className="mt-4 mb-1"
                                        left={<IconCalendarDollar />}
                                    />
                                    <Text.span
                                        value={t('managePlan.receivables.description')}
                                        contrast={6}
                                    />

                                    <Input.Choices
                                        className="mt-1"
                                        name="receivables-flow"
                                        disabled={pricingLocked}
                                        value={receivablesFlow}
                                        onInput={InputHandler.receivablesFlow}
                                        options={[
                                            {
                                                label: t('managePlan.receivables.anticipatedTitle'),
                                                description: t('managePlan.receivables.anticipatedDescription'),
                                                value: 'anticipated',
                                                left: <IconBolt />
                                            },
                                            {
                                                label: t('managePlan.receivables.extendedTitle'),
                                                description: t('managePlan.receivables.extendedDescription'),
                                                value: 'extended',
                                                left: <IconCalendarRepeat />
                                            }
                                        ]}
                                    />

                                    {pricingLocked && (
                                        <Text.caption
                                            contrast={6}
                                            className="mt-1"
                                            left={<IconLock />}
                                        >
                                            {t('managePlan.receivables.locked')}
                                        </Text.caption>
                                    )}
                                </>
                            )}
                        </div>
                    }

                    {info && (
                        <div className="plan-preview-column" ref={REF_previewColumn}>
                            <CheckoutPreview
                                info={info}
                                methods={activeMethods}
                                intervalLabel={intervalsConfig.find((option) => option.interval === info.interval)?.label}
                                stripeConnected={stripeConnected}
                                simulation={simulation}
                                region={previewRegion}
                                onRegionChange={setPreviewRegion}
                                onRetry={() => simulateInstallments(info, { force: true })}
                            />
                        </div>
                    )}
                </ div>
            }
            modalClass="cp-modal"
        />
    );
}
